import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActionArea,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useApi } from '../api/client';
import { Page } from '../components/Page';
import FilePreviewDialog from '../components/FilePreviewDialog';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'connecting', label: 'Connecting' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'no_response', label: 'No response' },
];

const STATUS_COLOR = {
  pending: 'default',
  connecting: 'info',
  accepted: 'success',
  meeting: 'primary',
  rejected: 'error',
  no_response: 'warning',
};

function isImage(mimeType) {
  return mimeType && (mimeType.startsWith('image/') || mimeType === 'image/jpg');
}

function buildProfilePreviewItems(fileUrls) {
  const items = [];
  const list = Array.isArray(fileUrls) ? fileUrls : [];
  for (let i = 0; i < list.length; i += 1) {
    const f = list[i];
    if (!f.url) continue;
    items.push({
      url: f.url,
      key: f.key,
      isImage: isImage(f.mimeType),
      mimeType: f.mimeType,
    });
  }
  return items;
}

function openProfilePreview(file, fileUrls, setPreviewFile) {
  if (!file.url) return;
  const items = buildProfilePreviewItems(fileUrls);
  let index = 0;
  for (let i = 0; i < items.length; i += 1) {
    if (file.key && items[i].key === file.key) {
      index = i;
      break;
    }
  }
  setPreviewFile({
    url: file.url,
    key: file.key,
    isImage: isImage(file.mimeType),
    mimeType: file.mimeType,
    items,
    index,
  });
}

export default function Matrimony() {
  const { request } = useApi();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', source: '', link: '', status: 'pending', notes: '' });
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null); // { url, isImage }

  async function loadList() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (searchQ.trim()) params.set('q', searchQ.trim());
      params.set('includeFileUrls', '1');
      const res = await request(`/api/matrimony?${params}`);
      if (res.ok) {
        const data = await res.json();
        setList(data);
      }
    } catch (e) {
      console.error('Matrimony list load failed', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadList();
  }, [statusFilter]);

  const openCreate = useCallback(() => {
    setEditingId(null);
    setForm({ name: '', source: '', link: '', status: 'pending', notes: '' });
    setFormError('');
    setPendingFiles([]);
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((profile) => {
    setEditingId(profile.id);
    setForm({
      name: profile.name || '',
      source: profile.source || '',
      link: profile.link || '',
      status: profile.status || 'pending',
      notes: profile.notes || '',
    });
    setFormError('');
    setFormOpen(true);
  }, []);

  const saveForm = useCallback(async () => {
    if (!form.name.trim()) {
      setFormError('Name is required');
      return;
    }
    setFormSaving(true);
    setFormError('');
    try {
      if (editingId) {
        const res = await request(`/api/matrimony/${editingId}`, {
          method: 'PATCH',
          body: JSON.stringify(form),
        });
        if (res.ok) {
          setFormOpen(false);
          loadList();
        } else {
          const data = await res.json().catch(() => ({}));
          setFormError(data.error || 'Update failed');
        }
      } else {
        const res = await request('/api/matrimony', {
          method: 'POST',
          body: JSON.stringify(form),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setFormError(data.error || 'Create failed');
          return;
        }
        const created = await res.json();
        for (const file of pendingFiles) {
          const formData = new FormData();
          formData.append('file', file);
          const upRes = await request(`/api/matrimony/${created.id}/files`, {
            method: 'POST',
            body: formData,
            headers: {},
          });
          if (!upRes.ok) {
            const data = await upRes.json().catch(() => ({}));
            setFormError(data.error || `Failed to upload ${file.name}`);
            return;
          }
        }
        setPendingFiles([]);
        setFormOpen(false);
        loadList();
      }
    } catch (e) {
      setFormError(e.message || 'Request failed');
    } finally {
      setFormSaving(false);
    }
  }, [editingId, form, request, loadList, pendingFiles]);

  const handleFileUpload = useCallback(
    async (profileId, file) => {
      if (!file) return;
      setUploadingId(profileId);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await request(`/api/matrimony/${profileId}/files`, {
          method: 'POST',
          body: formData,
          headers: {}, // let browser set Content-Type for multipart
        });
        if (res.ok) {
          loadList();
        }
      } catch (e) {
        console.error('Upload failed', e);
      } finally {
        setUploadingId(null);
      }
    },
    [request, loadList],
  );

  const removeFile = useCallback(
    async (profileId, key) => {
      try {
        const res = await request(`/api/matrimony/${profileId}/files?key=${encodeURIComponent(key)}`, {
          method: 'DELETE',
        });
        if (res.ok) {
          loadList();
        }
      } catch (e) {
        console.error('Remove file failed', e);
      }
    },
    [request, loadList],
  );

  const deleteProfile = useCallback(async () => {
    if (!deleteConfirm) return;
    try {
      const res = await request(`/api/matrimony/${deleteConfirm}`, { method: 'DELETE' });
      if (res.ok) {
        setDeleteConfirm(null);
        loadList();
      }
    } catch (e) {
      console.error('Delete failed', e);
    }
  }, [deleteConfirm, request, loadList]);

  return (
    <Page sx={{ maxWidth: '100%', overflowX: 'hidden' }}>
      <Stack spacing={2} sx={{ maxWidth: '100%', minWidth: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="h6">Matrimony</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={loadList} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Add profile
            </Button>
          </Stack>
        </Stack>

        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch" flexWrap="wrap" useFlexGap>
            <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center" sx={{ minWidth: 0 }}>
              {STATUS_OPTIONS.map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  onClick={() => setStatusFilter(opt.value)}
                  color={opt.value === 'all' ? 'default' : STATUS_COLOR[opt.value] || 'default'}
                  variant={statusFilter === opt.value ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                size="small"
                placeholder="Search by name or notes"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadList()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, minWidth: 120 }}
              />
              <Button size="small" variant="outlined" onClick={loadList} disabled={loading}>
                Search
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {loading ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Paper>
        ) : list.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No profiles. Add one to get started.</Typography>
          </Paper>
        ) : (
          <Grid container spacing={0} sx={{ maxWidth: '100%' }}>
            {list.map((profile) => (
              <Grid item xs={12} sm={6} key={profile.id} style={{ padding: '10px' }}>
                <Card variant="outlined" sx={{ height: '100%', minWidth: 0, overflow: 'visible', width: '100%', maxWidth: '100%' }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, maxWidth: '100%', minWidth: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
                      {(profile.fileUrls || []).map((f, i) => (
                        <Box
                          key={f.key || i}
                          onClick={() => openProfilePreview(f, profile.fileUrls, setPreviewFile)}
                          role="button"
                          sx={{
                            position: 'relative',
                            width: 56,
                            height: 56,
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: 'action.hover',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: f.url ? 'pointer' : 'default',
                          }}
                        >
                          {f.url && isImage(f.mimeType) ? <img src={f.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <PictureAsPdfIcon sx={{ fontSize: 28 }} />}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(profile.id, f.key);
                            }}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              bgcolor: 'rgba(0,0,0,0.5)',
                              color: 'white',
                              width: 20,
                              height: 20,
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                            }}
                            aria-label="Delete file"
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      ))}
                      {(!profile.fileUrls || profile.fileUrls.length === 0) && (
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ImageIcon sx={{ color: 'text.disabled' }} />
                        </Box>
                      )}
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={0.5} sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {profile.name}
                      </Typography>
                      <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
                        <IconButton size="small" onClick={() => openEdit(profile)} title="Edit" aria-label="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm(profile.id)} title="Delete" aria-label="Delete">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Status –
                        </Typography>
                        <Chip
                          label={STATUS_OPTIONS.find((o) => o.value === profile.status)?.label ?? profile.status}
                          size="small"
                          color={STATUS_COLOR[profile.status] || 'default'}
                          sx={{ height: 20 }}
                        />
                      </Stack>
                      {profile.source && (
                        <Typography variant="caption" color="text.secondary">
                          Source – {profile.source}
                        </Typography>
                      )}
                      {profile.link && (
                        <IconButton size="small" onClick={() => window.open(profile.link, '_blank')} title="Open link" aria-label="Open link">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    {profile.notes && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {profile.notes}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>

      <FilePreviewDialog
        preview={previewFile}
        onClose={() => setPreviewFile(null)}
        request={request}
        contentPath="/api/matrimony/files/content"
      />

      {/* Create/Edit form */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingId ? 'Edit profile' : 'Add profile'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Name" required fullWidth value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <TextField label="Source" fullWidth value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} />
            <TextField label="Link" fullWidth value={form.link} onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))} />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={form.status} label="Status" onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                {STATUS_OPTIONS.filter((o) => o.value !== 'all').map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Notes" fullWidth multiline rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            {!editingId && (
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Bio data (optional)
                </Typography>
                {pendingFiles.length > 0 && (
                  <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                    {pendingFiles.map((file, idx) => (
                      <ListItem
                        key={`${file.name}-${idx}`}
                        secondaryAction={
                          <IconButton size="small" edge="end" onClick={() => setPendingFiles((p) => p.filter((_, i) => i !== idx))} aria-label="Remove file">
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(1)} KB`} />
                      </ListItem>
                    ))}
                  </List>
                )}
                <Button variant="outlined" size="small" component="label" startIcon={<AddIcon />} disabled={formSaving}>
                  Add file
                  <input
                    type="file"
                    hidden
                    multiple
                    accept=".pdf,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
                    onChange={(e) => {
                      const files = e.target.files ? Array.from(e.target.files) : [];
                      if (files.length) setPendingFiles((p) => [...p, ...files]);
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Stack>
            )}
            {editingId && (
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Add more files
                </Typography>
                <Button variant="outlined" size="small" component="label" startIcon={uploadingId === editingId ? <CircularProgress size={16} /> : <AddIcon />} disabled={!!uploadingId}>
                  Add file
                  <input
                    type="file"
                    hidden
                    multiple
                    accept=".pdf,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
                    onChange={(e) => {
                      const files = e.target.files ? Array.from(e.target.files) : [];
                      files.forEach((file) => handleFileUpload(editingId, file));
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Stack>
            )}
            {formError && (
              <Typography variant="body2" color="error">
                {formError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveForm} disabled={formSaving}>
            {formSaving ? <CircularProgress size={24} /> : editingId ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete profile?</DialogTitle>
        <DialogContent>
          <Typography>This will remove the profile and all uploaded files. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteProfile}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
